import React, { CSSProperties } from "react"
import { Calendar } from "@walmart-web/livingdesign-icons"
import { useIntl } from "react-intl"
import { useApplicationContext } from "../../../context/applicationContext"
import { formatDateWithMonth } from "../../../helpers/temporal"
import {
	CLEAR_PERIOD_BUTTONS_FEATURE_FLAG,
	END_INPUT_LABEL_ID,
	END_RANGE_DATE_POSITION,
	START_INPUT_LABEL_ID,
	START_RANGE_DATE_POSITION,
} from "./constants"
import {
	CalendarHeaderProps,
	InternalDate,
	RangeCalendar,
} from "./MultiRangeCalendar.domain"
import { isFeatureEnabled } from "config/feature-flags"
import { COLOR_PALETTE } from "colors"

const CalendarHeader = ({
	ranges,
	focusedCalendarRange,
	onFocus = () => {},
	showCleanButtons,
	clearPeriods = () => {},
}: CalendarHeaderProps) => {
	const { locale } = useApplicationContext()
	const [focusedCalendarIndex, dateIndex] = focusedCalendarRange
	const intl = useIntl()

	const inputsCss = showCleanButtons
		? "multi-calendar-range__header-inputs-with-clean-buttons"
		: ""

	const dayInputCss = showCleanButtons
		? "multiple-date-range__day-input-with-clean-buttons"
		: ""

	const handleOnFocus = (newFocusedCalendarRange: number[]) => {
		onFocus(newFocusedCalendarRange)
	}

	const getInputValue = (date: InternalDate, id: string) => (
		<span>
			<b>{intl.formatMessage({ id: id })}</b>:{" "}
			{date ? formatDateWithMonth(date, locale) : "-"}
		</span>
	)

	const getStyleInput = (
		index: number,
		typeInput: number,
		styles: CSSProperties
	) => {
		const isCurrentIndex = focusedCalendarIndex === index
		return isCurrentIndex && dateIndex === typeInput ? styles : {}
	}

	return (
		<div className="multi-calendar-range__dates-header">
			{ranges.map(
				(
					{ label, startDate, endDate, color, id }: RangeCalendar,
					index: number
				) => {
					const styles: CSSProperties = {
						outline: `1px ${color} solid`,
					}
					const startStyles = getStyleInput(
						index,
						START_RANGE_DATE_POSITION,
						styles
					)
					const endStyles = getStyleInput(
						index,
						END_RANGE_DATE_POSITION,
						styles
					)

					return (
						<div key={id} className="multi-calendar-range__dates-config">
							<div className="multi-calendar-range__header-icons">
								<span style={{ color: COLOR_PALETTE.GRAY_160 }}>
									<Calendar className="multi-calendar-range__header-icon" />
									{intl.formatMessage({ id: label })}:
								</span>
							</div>
							<div
								data-testid="header-inputs"
								className={`multi-calendar-range__header-inputs ${inputsCss}`}
							>
								<div
									className={`multiple-date-range__day-input ${dayInputCss} multiple-date-range__day-input--start`}
									style={startStyles}
									data-testid={`input-start-${index}`}
									onClick={() =>
										handleOnFocus([index, START_RANGE_DATE_POSITION])
									}
								>
									{getInputValue(startDate, START_INPUT_LABEL_ID)}
								</div>
								<div
									className={`multiple-date-range__day-input ${dayInputCss} multiple-date-range__day-input--end`}
									style={endStyles}
									data-testid={`input-end-${index}`}
									onClick={() =>
										handleOnFocus([index, END_RANGE_DATE_POSITION])
									}
								>
									{getInputValue(endDate, END_INPUT_LABEL_ID)}
								</div>
							</div>
							{showCleanButtons &&
							isFeatureEnabled(CLEAR_PERIOD_BUTTONS_FEATURE_FLAG) ? (
								<div className="multi-calendar-range__clean-buttons">
									<span
										data-testid={`clean-button-${index}`}
										onClick={() => clearPeriods(index)}
									>
										{intl.formatMessage({
											id: "phronesis.calendar.clean-period",
										})}
									</span>
								</div>
							) : (
								<></>
							)}
						</div>
					)
				}
			)}
		</div>
	)
}

export default CalendarHeader
